import React, { useEffect, useMemo, useState } from "react";
import { useGetDatasets, useSearchDatasets } from "api/api-hooks/datasets-hooks"; // Import your custom hook
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { DropdownItem } from "utils/general-models";
import { T } from "services/lang/LanguageService";
import "./style.scss";

interface IDatasetMultiSelectProps {
  value?: string[] | null;
  onChange: (e: string[]) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
}

function DatasetMultiSelect(props: IDatasetMultiSelectProps) {
  const [selectedDatasets, setSelectedDatasets] = useState<DropdownItem<string>[] | null>(null);
  const [filteredDatasets, setFilteredDatasets] = useState<DropdownItem<string>[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const validIdentifiers = useMemo(() => props.value?.filter(id => id !== "") || [], [props.value]);

  const { data } = useGetDatasets(null, 100, validIdentifiers);

  const { data: searchResults } = useSearchDatasets(searchQuery, 100, {
    enabled: searchQuery.length >= 3,
  });

  useEffect(() => {
    if (validIdentifiers.length > 0 && data && props.value) {
      const apiSelections = data.map(
        it =>
          ({
            value: it.identifier,
            label: it.toString(),
          }) as DropdownItem<string>
      );

      const emptySelections = props.value
        .filter(it => it === "")
        .map(
          () =>
            ({
              value: "",
              label: "Empty",
            }) as DropdownItem<string>
        );

      setSelectedDatasets([...apiSelections, ...emptySelections]);
    }
  }, [data, validIdentifiers, props.value]);

  useEffect(() => {
    if (searchResults) {
      const selectedDatasetIdentifiers = selectedDatasets?.map(dataset => dataset.value) || [];
      const selections = searchResults
        .filter(it => !selectedDatasetIdentifiers.includes(it.identifier as string))
        .map(it => ({ value: it.identifier, label: it.toString() }) as DropdownItem<string>);
      setFilteredDatasets(selections);
    }
  }, [searchResults, selectedDatasets]);

  const searchDatasets = (e: AutoCompleteCompleteEvent) => {
    const query = e.query.trim();
    setSearchQuery(query);
  };

  const onChange = (e: AutoCompleteChangeEvent) => {
    setSelectedDatasets(e.value);

    if (typeof e.value === "string") return; // Ignore string auto complete dumb input

    props.onChange(e.value?.map((item: DropdownItem<string>) => item.value) || []);
    setSearchQuery("")
  };

  const chipTemplate = (item: DropdownItem<string>) => {
    return item.value.length > 0 ? (
      <div className="flex nowrap gap-1 align-items-baseline mr-2">
        <span className="w-16rem sm:w-full overflow-x-auto">{item.label}</span>
        <CopyTextWithButton text="" copyableText={item.value} />
        <ChartInDialogButton identifier={item.value} header={T("word.dataset")} />
      </div>
    ) : (
      <div className="flex nowrap gap-1 align-items-baseline mr-2">
        <span>{item.label}</span>
      </div>
    );
  };

  return (
    <AutoComplete
      placeholder={T("message.type-for-search-datasets")}
      multiple
      value={selectedDatasets}
      suggestions={filteredDatasets}
      onChange={onChange}
      completeMethod={searchDatasets}
      minLength={3}
      className="dataset-multi-select"
      field="label"
      style={{ width: "100%", ...props.style }}
      disabled={props.disabled}
      selectedItemTemplate={chipTemplate}
    />
  );
}

export default DatasetMultiSelect;
